import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
  Quiz as QuizIcon,
  Public as PublicIcon,
  Lock as PrivateIcon,
  Timer as TimerIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { useQuiz } from '../../contexts/QuizContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { vi } from 'date-fns/locale';

const QuizList: React.FC = () => {
  const navigate = useNavigate();
  const { canCreateQuizzes, user, canManageQuiz } = useAuth();
  const quizContext = useQuiz();
  const {
    quizzes,
    quizLoading,
    quizError,
    pagination,
    fetchQuizzes,
    deleteQuiz,
  } = quizContext;

  useEffect(() => {
    fetchQuizzes();
  }, [fetchQuizzes]);

  const handleDeleteQuiz = async (quizId: number, quizTitle: string) => {
    try {
      if (!deleteQuiz || typeof deleteQuiz !== 'function') {
        console.error('deleteQuiz function is not available');
        return;
      }
      
      if (window.confirm(`Bạn có chắc chắn muốn xóa bài tập "${quizTitle}"?`)) {
        const success = await deleteQuiz(quizId);
        if (success && fetchQuizzes && typeof fetchQuizzes === 'function') {
          // Refresh the list
          fetchQuizzes();
        }
      }
    } catch (error) {
      console.error('Error deleting quiz:', error);
    }
  };

  const handleShareQuiz = (quizId: number) => {
    try {
      const shareUrl = `${window.location.origin}/quiz/${quizId}`;
      navigator.clipboard.writeText(shareUrl).then(() => {
        alert('Link chia sẻ đã được sao chép vào clipboard!');
      }).catch(() => {
        alert('Không thể sao chép link chia sẻ');
      });
    } catch (error) {
      console.error('Error sharing quiz:', error);
    }
  };

  const formatTimeLimit = (minutes: number | null) => {
    if (!minutes) return 'Không giới hạn';
    if (minutes < 60) return `${minutes} phút`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  };

  if (quizLoading && (!quizzes || quizzes.length === 0)) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box py={3}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" gutterBottom>
            Quản lý bài tập trắc nghiệm
          </Typography>
          {/* Only show Create Quiz button for admin and teacher */}
          {canCreateQuizzes() && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/quiz/create')}
            >
              Tạo bài tập mới
            </Button>
          )}
        </Box>

        {/* Error Alert */}
        {quizError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {quizError}
          </Alert>
        )}

        {/* Quiz Grid */}
        {(!quizzes || quizzes.length === 0) && !quizLoading ? (
          <Box textAlign="center" py={8}>
            <QuizIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Chưa có bài tập nào
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {canCreateQuizzes() 
                ? 'Tạo bài tập đầu tiên từ các câu hỏi đã có'
                : 'Chưa có bài tập nào được tạo'
              }
            </Typography>
            {canCreateQuizzes() && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/quiz/create')}
              >
                Tạo bài tập đầu tiên
              </Button>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: 3,
            }}
          >
            {(quizzes || []).map((quiz) => (
              <Card 
                key={quiz.id || Math.random()} 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  cursor: 'default'
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  {/* Quiz Title */}
                  <Typography variant="h6" component="h2" gutterBottom noWrap>
                    {quiz.title || 'Bài tập không có tiêu đề'}
                  </Typography>

                  {/* Quiz Description */}
                  {quiz.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {quiz.description}
                    </Typography>
                  )}

                  {/* Quiz Stats */}
                  <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                    <Chip
                      size="small"
                      icon={<QuizIcon />}
                      label={`${quiz.questions_count || 0} câu hỏi`}
                      variant="outlined"
                      sx={{ cursor: 'default', pointerEvents: 'none' }}
                    />
                    <Chip
                      size="small"
                      icon={<PeopleIcon />}
                      label={`${quiz.attempts_count || 0} lượt làm`}
                      variant="outlined"
                      sx={{ cursor: 'default', pointerEvents: 'none' }}
                    />
                    {quiz.time_limit && (
                      <Chip
                        size="small"
                        icon={<TimerIcon />}
                        label={formatTimeLimit(quiz.time_limit)}
                        variant="outlined"
                        sx={{ cursor: 'default', pointerEvents: 'none' }}
                      />
                    )}
                  </Box>

                  {/* Quiz Settings */}
                  <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                    <Chip
                      size="small"
                      icon={quiz.is_public ? <PublicIcon /> : <PrivateIcon />}
                      label={quiz.is_public ? 'Công khai' : 'Riêng tư'}
                      color={quiz.is_public ? 'success' : 'default'}
                      sx={{ cursor: 'default', pointerEvents: 'none' }}
                    />
                  </Box>

                  {/* Created Date */}
                  <Typography variant="caption" color="text.secondary">
                    {quiz.created_at ? (
                      `Tạo ${formatDistanceToNow(new Date(quiz.created_at), { 
                        addSuffix: true, 
                        locale: vi 
                      })}`
                    ) : (
                      'Ngày tạo không xác định'
                    )}
                  </Typography>
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Box>
                    {/* Only show edit/delete/stats for quiz owner or admin */}
                    {canCreateQuizzes() && user && quiz.user_id && canManageQuiz(quiz.user_id) && (
                      <>
                        <Tooltip title="Chỉnh sửa">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (quiz.id) {
                                navigate(`/quiz/${quiz.id}/edit`);
                              }
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Xem danh sách người làm bài">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => {
                              if (quiz.id) {
                                navigate(`/quiz/${quiz.id}/attempts`);
                              }
                            }}
                          >
                            <AssessmentIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Xóa">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              if (quiz.id && quiz.title) {
                                handleDeleteQuiz(quiz.id, quiz.title);
                              }
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    <Tooltip title="Chia sẻ">
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (quiz.id) {
                            handleShareQuiz(quiz.id);
                          }
                        }}
                      >
                        <ShareIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => {
                      if (quiz.id && navigate && typeof navigate === 'function') {
                        navigate(`/quiz/${quiz.id}/take`);
                      }
                    }}
                  >
                    {quiz.is_public ? 'Làm bài' : 'Làm bài (riêng tư)'}
                  </Button>
                </CardActions>
              </Card>
            ))}
          </Box>
        )}

        {/* Pagination */}
        {pagination.total > pagination.perPage && (
          <Box display="flex" justifyContent="center" mt={4}>
            <Typography variant="body2" color="text.secondary">
              Hiển thị {Math.min(pagination.perPage, pagination.total)} / {pagination.total} bài tập
              {/* TODO: Add pagination component */}
            </Typography>
          </Box>
        )}

        {/* Loading Overlay */}
        {quizLoading && quizzes.length > 0 && (
          <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bgcolor="rgba(255, 255, 255, 0.7)"
            display="flex"
            alignItems="center"
            justifyContent="center"
            zIndex={9999}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default QuizList; 